import { lazy, useState } from 'react';
import {
  Box,
  Text,
  Group,
  Badge,
  Avatar,
  ScrollArea,
  useMantineTheme,
  useComputedColorScheme,
  SegmentedControl,
  Tooltip,
  AvatarGroup,
} from '@mantine/core';
import { IconBriefcase, IconClock, IconUsers } from '@tabler/icons-react';
import { WidgetProps, ProjectData, UserData } from '../types';
import { registerWidget } from '../registry';
import { FadeIn } from '../../../animations';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// ProjectList Widget Component
// ============================================================================

function ProjectListWidget({ globalData, isCollapsed, filters, onFilterChange }: WidgetProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Filter state
  const statusFilter = (filters?.status as string) || 'all';

  if (isCollapsed) return null;

  const projects = globalData?.projects || [];
  const users = globalData?.users || [];

  // Filter projects by status
  const filteredProjects =
    statusFilter === 'all'
      ? projects
      : projects.filter((p) => p.status === statusFilter);

  // Sort by due date
  const sortedProjects = [...filteredProjects].sort(
    (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
  );

  const getStatusColor = (status: ProjectData['status']) => {
    const colors: Record<ProjectData['status'], string> = {
      planning: 'gray',
      'in-progress': 'blue',
      'on-hold': 'yellow',
      completed: 'green',
      cancelled: 'red',
    };
    return colors[status];
  };

  const getPriorityColor = (priority: ProjectData['priority']) => {
    const colors: Record<ProjectData['priority'], string> = {
      low: 'gray',
      medium: 'blue',
      high: 'orange',
      urgent: 'red',
    };
    return colors[priority];
  };

  const getProjectMembers = (memberIds: string[]) => {
    return memberIds.map((id) => users.find((u) => u.id === id)).filter(Boolean) as UserData[];
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays <= 7) return `${diffDays} days`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <FadeIn style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Filter controls */}
        <SegmentedControl
          size="xs"
          mb="sm"
          fullWidth
          value={statusFilter}
          onChange={(value) => onFilterChange?.({ ...filters, status: value })}
          data={[
            { label: 'All', value: 'all' },
            { label: 'Active', value: 'in-progress' },
            { label: 'Planning', value: 'planning' },
            { label: 'On Hold', value: 'on-hold' },
          ]}
        />

        {/* Project list */}
        <ScrollArea style={{ flex: 1, minHeight: 0 }}>
          {sortedProjects.length === 0 ? (
            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 100,
                color: theme.colors.gray[5],
              }}
            >
              <IconBriefcase size={32} stroke={1.5} />
              <Text size="sm" mt="xs" style={accessibilityStyles}>
                No projects found
              </Text>
            </Box>
          ) : (
            sortedProjects.slice(0, 8).map((project) => {
              const members = getProjectMembers(project.memberIds);

              return (
                <Box
                  key={project.id}
                  p="sm"
                  mb="xs"
                  style={{
                    backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                    borderRadius: theme.radius.md,
                    borderLeft: `3px solid ${theme.colors[getPriorityColor(project.priority)][5]}`,
                  }}
                >
                  <Group justify="space-between" mb="xs" wrap="nowrap">
                    <Text size="sm" fw={600} lineClamp={1} style={{ ...accessibilityStyles, flex: 1 }}>
                      {project.name}
                    </Text>
                    <Badge size="xs" color={getStatusColor(project.status)} variant="light">
                      {project.status.replace('-', ' ')}
                    </Badge>
                  </Group>

                  <Text size="xs" c="dimmed" lineClamp={1} mb="xs" style={accessibilityStyles}>
                    {project.description}
                  </Text>

                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="xs" wrap="nowrap">
                      <Group gap={4}>
                        <IconClock size={12} color={theme.colors.gray[5]} />
                        <Text size="xs" c="dimmed" style={accessibilityStyles}>
                          {formatDate(project.dueDate)}
                        </Text>
                      </Group>
                    </Group>

                    {members.length > 0 && (
                      <AvatarGroup>
                        {members.slice(0, 3).map((member) => (
                          <Tooltip key={member.id} label={member.name} withArrow>
                            <Avatar
                              src={member.avatar}
                              size="xs"
                              radius="xl"
                              color="blue"
                            >
                              {member.name.charAt(0)}
                            </Avatar>
                          </Tooltip>
                        ))}
                        {members.length > 3 && (
                          <Avatar size="xs" radius="xl" color="gray">
                            +{members.length - 3}
                          </Avatar>
                        )}
                      </AvatarGroup>
                    )}
                  </Group>
                </Box>
              );
            })
          )}
        </ScrollArea>
      </Box>
    </FadeIn>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'project-list',
  name: 'Project List',
  description: 'Filterable list of projects with status, priority, and team info',
  icon: IconBriefcase,
  category: 'projects',
  component: lazy(() => Promise.resolve({ default: ProjectListWidget })),
  sizes: {
    base: { cols: 12, rows: 6 },
    sm: { cols: 12, rows: 6 },
    md: { cols: 6, rows: 6 },
    lg: { cols: 6, rows: 6 },
    xl: { cols: 4, rows: 6 },
    tv: { cols: 4, rows: 5 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'tall', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 6, rows: 4 },
        md: { cols: 4, rows: 4 },
        lg: { cols: 4, rows: 4 },
        xl: { cols: 3, rows: 4 },
        tv: { cols: 3, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 6 },
        sm: { cols: 12, rows: 6 },
        md: { cols: 6, rows: 6 },
        lg: { cols: 6, rows: 6 },
        xl: { cols: 4, rows: 6 },
        tv: { cols: 4, rows: 5 },
      },
      tall: {
        base: { cols: 12, rows: 8 },
        sm: { cols: 12, rows: 8 },
        md: { cols: 6, rows: 8 },
        lg: { cols: 6, rows: 8 },
        xl: { cols: 4, rows: 8 },
        tv: { cols: 4, rows: 7 },
      },
      wide: {
        base: { cols: 12, rows: 5 },
        sm: { cols: 12, rows: 5 },
        md: { cols: 12, rows: 5 },
        lg: { cols: 8, rows: 5 },
        xl: { cols: 8, rows: 5 },
        tv: { cols: 8, rows: 4 },
      },
    },
  },
  data: {
    sourceType: 'global',
    globalDataKeys: ['projects', 'users'],
    defaultFilters: { status: 'all' },
  },
  filterLocation: 'inline',
  hasSettings: true,
});

export default ProjectListWidget;
